import { useEffect, useRef } from "react";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { listNotifications } from "../api";
import { getNotificationsClient } from "../services";
import { useNotificationsStore } from "../stores/notificationsStore";
import type { NotificationType } from "../types";

/** Quantas notificações carregar no histórico inicial / reconciliação. */
const HISTORY_LIMIT = 50;

/**
 * Toast genérico por tipo. Com o contrato slim (option C, ADR-0025) o stream SSE
 * só carrega `{id,type,unread}` — não há payload, então o toast é genérico (o
 * detalhe aparece na lista, reconciliada via REST). O texto vem do i18n
 * (`backoffice:notifications.types.<tipo>.toast`), idêntico ao usado na lista.
 */
function toastFor(type: NotificationType): void {
  const message = i18n.t(`backoffice:notifications.types.${type}.toast`);
  switch (type) {
    case "AdminTwoFactorReset":
      toast.warning(message);
      return;
    case "AdminRoleChanged":
    case "AdminAccountStatusChanged":
      toast.info(message);
      return;
    default:
      toast.message(message);
  }
}

/**
 * Liga o lifecycle de autenticação ao cliente SSE de notificações.
 * Deve ser montado uma única vez no AppLayout (acima das rotas autenticadas).
 *
 * Estratégia (fonte de verdade no servidor central de notificações, option C):
 * - carrega o histórico persistido via REST ao iniciar e a cada (re)conexão SSE
 *   (reconciliação — cobre sinais perdidos enquanto offline);
 * - o evento SSE `notification` é só uma dica slim `{id,type,unread}`: atualiza a
 *   contagem de não lidas, relê o histórico (REST) e dispara um toast genérico;
 * - o `heartbeat` cura o drift do badge entre reconciliações.
 */
export function useNotificationsConnection(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // `isAuthenticated` é persistido (localStorage), mas o accessToken é só em memória
  // (ADR-0017). Em cold load ele começa null até o primeiro refresh, então o SSE não
  // pode subir só com isAuthenticated — iria com Authorization vazio e tomaria 401 sem
  // reconectar. Observamos a PRESENÇA do token (não o valor) para subir quando ele
  // chega e não derrubar a conexão a cada rotação.
  const hasToken = useAuthStore((s) => s.accessToken !== null);
  const startedFor = useRef<boolean>(false);

  useEffect(() => {
    const client = getNotificationsClient();
    const store = useNotificationsStore.getState();

    if (!isAuthenticated || !hasToken) {
      if (startedFor.current) {
        void client.stop();
        store.clear();
        startedFor.current = false;
      }
      return;
    }

    if (startedFor.current) return;
    startedFor.current = true;

    // getToken relê o token em memória; refreshSession renova de verdade (POST
    // /auth/refresh, single-flight no authStore) quando o servidor fecha o stream
    // no exp do token ou a reconexão toma 401 — sem isso o reconnect reusaria o
    // token expirado para sempre (401 em loop até o F5).
    client.build(
      () => useAuthStore.getState().accessToken,
      async () =>
        (await useAuthStore.getState().refreshSession())
          ? useAuthStore.getState().accessToken
          : null,
    );

    async function loadHistory(): Promise<void> {
      try {
        const page = await listNotifications({ limit: HISTORY_LIMIT });
        useNotificationsStore.getState().hydrate(page.items, page.unreadCount);
      } catch (err) {
        console.error("[notifications] failed to load history:", err);
      }
    }

    client.onStatusChange((status) => {
      useNotificationsStore.getState().setConnectionStatus(status);
      // Reconcilia ao (re)conectar: relê o histórico e cura sinais perdidos.
      if (status === "connected") void loadHistory();
    });

    // Carrega o histórico já de cara (mesmo que o SSE demore ou caia, o sino/lista
    // mostram o que está persistido).
    void loadHistory();

    // Sinal slim `notification`: sem payload. Confia na contagem do servidor,
    // reconcilia a lista via REST e dispara um toast genérico por tipo.
    client.onNotification((sig) => {
      useNotificationsStore.getState().setUnreadCount(sig.unread);
      void loadHistory();
      toastFor(sig.type);
    });

    // Heartbeat: só a contagem de não lidas (cura o drift do badge).
    client.onHeartbeat((unread) => useNotificationsStore.getState().setUnreadCount(unread));

    client.start().catch((err) => {
      console.error("[notifications] failed to start client:", err);
    });

    return () => {
      // Em StrictMode o effect roda 2x; o flag startedFor evita rebuild.
      // No unmount real (logout em outra aba, etc) deixamos limpar.
      if (!useAuthStore.getState().isAuthenticated) {
        void client.stop();
        useNotificationsStore.getState().clear();
        startedFor.current = false;
      }
    };
  }, [isAuthenticated, hasToken]);
}
